import { Gauge } from 'lucide-react';

import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';

import { Datatable, TableSettingsMenu } from '@@/datatables';
import { TableSettingsMenuAutoRefresh } from '@@/datatables/TableSettingsMenuAutoRefresh';
import { useTableStateWithStorage } from '@@/datatables/useTableState';
import {
  BasicTableSettings,
  refreshableSettings,
  RefreshableTableSettings,
} from '@@/datatables/types';

import { useNamespaceQuery } from '../../queries/useNamespaceQuery';

import { useColumns } from './columns';
import { EffectiveCeilingDials } from './EffectiveCeilingDials';

interface TableSettings extends BasicTableSettings, RefreshableTableSettings {}

/**
 * Lists every resource quota in a namespace, not only the one Portainer
 * manages. A namespace may hold several and Kubernetes enforces them all, so
 * omitting the others hides what is actually constraining the workloads.
 */
export function ResourceQuotasDatatable({ namespace }: { namespace: string }) {
  const environmentId = useEnvironmentId();
  const tableState = useTableStateWithStorage<TableSettings>(
    'kube-namespace-resource-quotas',
    'name',
    (set) => ({
      ...refreshableSettings(set),
    })
  );

  const namespaceQuery = useNamespaceQuery(environmentId, namespace, {
    params: { withResourceQuota: 'true' },
    refetchInterval: tableState.autoRefreshRateMS,
  });

  const quotas = namespaceQuery.data?.ResourceQuotas ?? [];
  const columns = useColumns();

  return (
    <Datatable
      dataset={quotas}
      settingsManager={tableState}
      columns={columns}
      disableSelect
      title="Resource quotas"
      titleIcon={Gauge}
      isLoading={namespaceQuery.isLoading}
      emptyContentLabel="This namespace has no resource quotas"
      getRowId={(row) => row.metadata?.uid ?? row.metadata?.name ?? ''}
      description={
        <EffectiveCeilingDials
          ceiling={namespaceQuery.data?.EffectiveCeiling ?? {}}
          resourceQuotas={namespaceQuery.data?.ResourceQuotas ?? []}
        />
      }
      renderTableSettings={() => (
        <TableSettingsMenu>
          <TableSettingsMenuAutoRefresh
            value={tableState.autoRefreshRateMS}
            onChange={tableState.setAutoRefreshRate}
          />
        </TableSettingsMenu>
      )}
      data-cy="namespace-resource-quotas-datatable"
    />
  );
}
